import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";

const STORAGE_KEY = "dismissed_suggestions";

export function useDismissedSuggestions() {
  const { user } = useAuth();
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);

  // Load dismissed IDs from localStorage on mount
  useEffect(() => {
    if (!user?.id) return;
    
    try {
      const stored = localStorage.getItem(`${STORAGE_KEY}_${user.id}`);
      if (stored) {
        setDismissedIds(JSON.parse(stored));
      }
    } catch (error) {
      console.error("Error loading dismissed suggestions:", error);
    }
  }, [user?.id]);

  // Dismiss a suggestion - persistence-first pattern
  const dismiss = (userId: string) => {
    if (!user?.id) return;
    
    const updated = [...dismissedIds, userId];
    
    // Persist FIRST - guarantees survival on crash
    try {
      localStorage.setItem(`${STORAGE_KEY}_${user.id}`, JSON.stringify(updated));
      // Only update state AFTER successful persistence
      setDismissedIds(updated);
    } catch (error) {
      console.error("Error saving dismissed suggestion:", error);
      // State NOT updated if persistence fails - maintains consistency
    }
  };

  // Clear all dismissed suggestions - persistence-first pattern
  const clearAll = () => {
    if (!user?.id) return;
    
    // Persist FIRST
    try {
      localStorage.removeItem(`${STORAGE_KEY}_${user.id}`);
      // Only update state AFTER successful persistence
      setDismissedIds([]);
    } catch (error) {
      console.error("Error clearing dismissed suggestions:", error);
      // State NOT updated if persistence fails
    }
  };

  return {
    dismissedIds,
    dismiss,
    clearAll,
  };
}
